<template>
  <Breadcrumb :product="productData" :key="`breadcrumb-${productData.id}`" />
  <Buybox :product="productData" :key="`buybox-${productData.id}`" />
  <ProductStickyBar :product="productData" :key="`sticky-${productData.id}`" />
</template>

<script setup>
  import { ref, onMounted, onUnmounted, nextTick } from 'vue';
  import { useStore } from 'vuex';
  import { parseJSON } from '../../utils/helper-functions';
  import Breadcrumb from './components/Breadcrumb.vue';
  import Buybox from './components/buybox.vue';
  import ProductStickyBar from './components/product-sticky-bar.vue';

  const props = defineProps({
    data: {
      type: Object,
      default: () => ({}),
    },
  });

  const store = useStore();

  // Reactive product data. Swapping this (plus the components' :key) re-renders the
  // whole buy box with a different product's data — no full page reload.
  const productData = ref(props.data);
  const isSwitching = ref(false);
  let pendingSwitch = null;
  let currentPath = window.location.pathname;

  // Cache of in-flight/resolved fetches, keyed by URL. Storing the promise (not
  // just the result) dedupes a prefetch-on-hover with the click that follows it.
  const productCache = new Map();

  onMounted(() => {
    store.dispatch('fetchWishlistItems');
    window.addEventListener('refreshWishlist', refreshWishlist);
    window.addEventListener('pdp:switch-style', onSwitchStyle);
    window.addEventListener('pdp:prefetch-style', onPrefetchStyle);
    window.addEventListener('popstate', onPopState);
  });

  onUnmounted(() => {
    window.removeEventListener('refreshWishlist', refreshWishlist);
    window.removeEventListener('pdp:switch-style', onSwitchStyle);
    window.removeEventListener('pdp:prefetch-style', onPrefetchStyle);
    window.removeEventListener('popstate', onPopState);
  });

  const refreshWishlist = () => {
    store.dispatch('fetchWishlistItems');
  };

  const onSwitchStyle = (event) => {
    const { url } = event.detail || {};
    if (url) loadProduct(url, { push: true });
  };

  // Warm the cache on hover/touch so the click that follows is instant.
  const onPrefetchStyle = (event) => {
    const { url } = event.detail || {};
    if (url) fetchProductData(url).catch(() => {});
  };

  // Keep the in-place view in sync when the user hits back/forward — but ignore
  // same-product history changes (e.g. hash links like #reviews) so we don't
  // reload the product that's already showing.
  const onPopState = () => {
    if (window.location.pathname === currentPath) return;
    loadProduct(window.location.href, { push: false });
  };

  /**
   * Fetch a product's product-main-new data and swap it in place.
   * Falls back to a normal navigation if anything goes wrong.
   */
  const loadProduct = async (url, { push }) => {
    // Last-click-wins: if a switch is already running, remember the latest request
    // and run it once the current one settles.
    if (isSwitching.value) {
      pendingSwitch = { url, push };
      return;
    }
    isSwitching.value = true;
    document.body.style.cursor = 'progress';

    try {
      const page = await fetchProductData(url);

      // Clear the previous product's selected options before rendering the new one.
      store.dispatch('resetPDPProductForm');

      // 1. Swap the Vue buy box (product-main-new) via reactive data + :key remount.
      productData.value = page.data;

      // 2. Swap the other server-rendered sections (reviews, faqs, stewardship,
      //    recommendations) from the same fetched page.
      applySections(page.sections);

      // 3. Keep document metadata / URL in sync.
      applyMeta(page.meta);
      if (push) {
        window.history.pushState({ pdpUrl: url }, '', url);
      }
      currentPath = new URL(url, window.location.origin).pathname;

      // 4. Navigation side effects that a real page load would perform.
      recordRecentlyViewed(page.data?.handle);
      trackProductView(page.data);
      announce(`Showing ${page.data?.title || 'product'}`);

      // Scroll to top for a fresh selection, but leave scroll alone on back/forward
      // so the browser can restore the user's previous position.
      if (push) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }

      // Once the new markup is in the DOM, let the page re-initialize the JS that
      // powers the swapped sections (Yotpo, accordions, recommendations, video…).
      await nextTick();
      window.dispatchEvent(new CustomEvent('pdp:sections-refreshed', { detail: { url } }));
    } catch (error) {
      // Graceful fallback: behave like the original link.
      window.location.href = url;
    } finally {
      isSwitching.value = false;
      document.body.style.cursor = '';

      // Run the most recent request that arrived while this one was in flight.
      if (pendingSwitch && pendingSwitch.url !== url) {
        const next = pendingSwitch;
        pendingSwitch = null;
        loadProduct(next.url, { push: next.push });
      } else {
        pendingSwitch = null;
      }
    }
  };

  // Server-rendered PDP sections we refresh on switch, keyed by a stable content
  // selector. We match on content — NOT the shopify-section wrapper id — because
  // the id differs between product templates (e.g. product-new vs
  // product-new-with-video the recommendation reels key is auto-generated).
  const PDP_SECTION_SELECTORS = {
    reviews: '#reviews',
    faqs: '.product-faq',
    stewardship: '.product-stewardship, .video-showcase',
    recommendations: '.recommendation-reels',
  };

  const findSectionWrapper = (root, selector) => {
    return root.querySelector(selector)?.closest('.shopify-section') || null;
  };

  // Replace each swapped section's markup in place. Always walk every known
  // section type — not just ones present in the fetch — so switching to a
  // product that omits stewardship / faqs / etc. clears the previous product's
  // markup instead of leaving it stale. Match the current wrapper by its
  // shopify-section id first (stable for most sections, and works even when
  // the current section is empty), then fall back to the content selector for
  // auto-keyed sections whose id differs between templates (recommendations).
  const applySections = (sections) => {
    if (!sections) return;
    Object.keys(PDP_SECTION_SELECTORS).forEach((type) => {
      const section = sections[type];
      const el =
        (section?.id && document.getElementById(section.id)) ||
        findSectionWrapper(document, PDP_SECTION_SELECTORS[type]);
      if (el) el.innerHTML = section?.html ?? '';
    });
  };

  /**
   * Fetch (and cache) a product's PDP payload: the #productJson for the buy box,
   * the other sections' HTML, and the document title. All from a single request.
   * Failed fetches are evicted so a later attempt can retry.
   */
  const fetchProductData = (url) => {
    if (productCache.has(url)) return productCache.get(url);

    const request = extractPageData(url)
      .then((page) => {
        if (!page) throw new Error('productJson not found for ' + url);
        return page;
      })
      .catch((error) => {
        productCache.delete(url);
        throw error;
      });

    productCache.set(url, request);
    return request;
  };

  const extractPageData = async (url) => {
    const response = await fetch(url);
    if (!response.ok) return null;

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');

    const jsonEl = doc.querySelector('#productJson');
    if (!jsonEl) return null;

    // Capture each known PDP section's markup + wrapper id by type (never the Vue
    // app section). Storing the id lets applySections match the current wrapper
    // even when the current product's section is empty.
    const sections = {};
    Object.keys(PDP_SECTION_SELECTORS).forEach((type) => {
      const el = findSectionWrapper(doc, PDP_SECTION_SELECTORS[type]);
      if (el) sections[type] = { id: el.id, html: el.innerHTML };
    });

    // Managed head metadata. Captured as the full set of serialized nodes so it
    // can be swapped wholesale — every og:* (incl. all og:image / secure_url and
    // og:price), every twitter:*, and the theme JSON-LD — rather than
    // cherry-picking individual fields (which leaves stale tags behind).
    const themeLd = [...doc.head.querySelectorAll('script[type="application/ld+json"]')].filter(isThemeLd);

    return {
      data: parseJSON(jsonEl.textContent),
      sections,
      meta: {
        title: doc.title,
        canonical: doc.querySelector('link[rel="canonical"]')?.getAttribute('href'),
        description: doc.querySelector('meta[name="description"]')?.getAttribute('content'),
        og: [...doc.querySelectorAll('meta[property^="og:"]')].map((el) => el.outerHTML),
        twitter: [...doc.querySelectorAll('meta[name^="twitter:"]')].map((el) => el.outerHTML),
        themeLd: themeLd.map((el) => el.outerHTML),
      },
    };
  };

  // --- Navigation side effects (parity with a real page load) -----------------

  const applyMeta = (meta) => {
    if (!meta) return;
    if (meta.title) document.title = meta.title;

    const setAttr = (selector, attr, value) => {
      if (value == null) return;
      const el = document.querySelector(selector);
      if (el) el.setAttribute(attr, value);
    };
    setAttr('link[rel="canonical"]', 'href', meta.canonical);
    setAttr('meta[name="description"]', 'content', meta.description);

    // Swap the whole og:* and twitter:* sets (handles multiple og:image tags,
    // og:price, og:description, twitter title/description, etc.).
    replaceHeadGroup('meta[property^="og:"]', meta.og);
    replaceHeadGroup('meta[name^="twitter:"]', meta.twitter);

    // Swap the theme's JSON-LD (Product, BreadcrumbList, FAQPage) for the new
    // product's set. A product without FAQs drops the old FAQPage block.
    if (Array.isArray(meta.themeLd)) {
      document.head.querySelectorAll('script[type="application/ld+json"]').forEach((el) => {
        if (isThemeLd(el)) el.remove();
      });
      appendToHead(meta.themeLd);
    }
  };

  // Remove every current node matching selector from <head>, then append the
  // captured replacements (each an outerHTML string).
  const replaceHeadGroup = (selector, htmlList) => {
    if (!Array.isArray(htmlList)) return;
    document.head.querySelectorAll(selector).forEach((el) => el.remove());
    appendToHead(htmlList);
  };

  // Append each outerHTML string to <head> as a node.
  const appendToHead = (htmlList) => {
    htmlList.forEach((html) => {
      const tpl = document.createElement('template');
      tpl.innerHTML = html.trim();
      const node = tpl.content.firstElementChild;
      if (node) document.head.appendChild(node);
    });
  };

  // JSON-LD blocks the theme renders per product (json-ld-schema.liquid). Other
  // ld+json (e.g. injected by apps) is left alone.
  const THEME_LD_TYPES = ['Product', 'BreadcrumbList', 'FAQPage'];
  const isThemeLd = (script) => {
    try {
      const parsed = JSON.parse(script.textContent);
      return Boolean(parsed) && THEME_LD_TYPES.includes(parsed['@type']);
    } catch (error) {
      return false;
    }
  };

  // Announce the content change to screen readers (soft-nav has no page load).
  const announce = (message) => {
    let region = document.getElementById('pdp-live-region');
    if (!region) {
      region = document.createElement('div');
      region.id = 'pdp-live-region';
      region.setAttribute('aria-live', 'polite');
      region.setAttribute('role', 'status');
      Object.assign(region.style, {
        position: 'absolute',
        width: '1px',
        height: '1px',
        margin: '-1px',
        padding: '0',
        overflow: 'hidden',
        clip: 'rect(0 0 0 0)',
        whiteSpace: 'nowrap',
        border: '0',
      });
      document.body.appendChild(region);
    }
    region.textContent = message;
  };

  // Mirror the recently-viewed.liquid write so switched-to products are recorded.
  const recordRecentlyViewed = (handle) => {
    if (!handle) return;
    try {
      const key = 'recentlyViewedProducts';
      const list = JSON.parse(localStorage.getItem(key) || '[]');
      list.unshift(handle);
      localStorage.setItem(key, JSON.stringify([...new Set(list)].slice(0, 8)));
    } catch (error) {
      /* localStorage unavailable — non-critical */
    }
  };

  // Re-fire the product-view tracking that inline scripts fire on a real page
  // load (GTM/Meta ViewContent + Klaviyo Viewed Product), and refresh the shared
  // Klaviyo ATC payload so "Added to Cart" tracks the product currently on screen.
  const toAbsoluteImageUrl = (src) => {
    if (!src) return '';
    if (/^https?:/i.test(src)) return src;
    if (src.startsWith('//')) return `https:${src}`;
    return src;
  };

  const formatKlMoney = (cents) => {
    const amount = Number(cents || 0) / 100;
    if (typeof window.formattedPrice === 'function') {
      return window.formattedPrice(amount);
    }
    return `$${amount.toFixed(2)}`;
  };

  const trackProductView = (p) => {
    if (!p) return;
    const priceCents = Number(p.price || 0);
    const compareCents = Number(p.compare_at_price || 0);
    const price = priceCents / 100;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      track: 'ViewContent',
      content_ids: String(p.id),
      product_name: p.title,
      category: p.type || '',
      content_type: 'product',
      value: price.toFixed(2),
      currency: window.Shopify?.currency?.active || 'USD',
    });

    const klItem = {
      Name: p.title,
      ProductID: p.id,
      Categories: p.collections || [],
      ImageURL: toAbsoluteImageUrl(p.images?.[0]?.src),
      URL: p.handle
        ? `${window.location.origin}/products/${p.handle}`
        : window.location.href,
      Brand: p.vendor || '',
      Price: formatKlMoney(priceCents),
      CompareAtPrice: formatKlMoney(compareCents),
    };
    // Shared with product-third-party.liquid's delegated ATC listener.
    window.__stioKlItem = klItem;

    if (Array.isArray(window._learnq)) {
      window._learnq.push(['track', 'Viewed Product', klItem]);
      window._learnq.push([
        'trackViewedItem',
        {
          Title: klItem.Name,
          ItemId: klItem.ProductID,
          Categories: klItem.Categories,
          ImageUrl: klItem.ImageURL,
          Url: klItem.URL,
          Metadata: {
            Brand: klItem.Brand,
            Price: klItem.Price,
            CompareAtPrice: klItem.CompareAtPrice,
          },
        },
      ]);
    }
  };
</script>
